// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router';
import HomeComponent from '../components/homeComponent.vue';
import SearchResults from '../components/searchResults.vue';
import NowPlaying from '../components/nowPlaying.vue';
import ArtistComponent from '../components/artistComponent.vue';
import PlaylistComponent from '../components/playlistComponent.vue';
import LoginComponent from '../components/loginComponent.vue';
import SignupComponent from '../components/signupComponent.vue';
import AccountComponent from '../components/accountComponent.vue';
import verifyEmailComponent from '../components/verifyEmail.vue';
import ForgotPasswordComponent from '../components/forgotPasswordComponent.vue';
import ResetPasswordComponent from '../components/resetPasswordComponent.vue';
import LikedSongsComponent from '../components/likedSongsComponent.vue';
import PlaylistsListComponent from '../components/playlistsListComponent.vue';
import UserPlaylistDetailComponent from '../components/userPlaylistDetailComponent.vue';
import { useUserStore } from '../stores/user';
import GenrePage from '../components/genrePage.vue';
import { useFeedStore } from '../stores/feed.js';

const routes = [
  { path: '/', name: 'home', component: HomeComponent },
  { path: '/search', name: 'search', component: SearchResults },
  { path: '/playing/:videoId', name: 'NowPlaying', component: NowPlaying },
  { path: '/artist/:artistId', name: 'artist', component: ArtistComponent },
  { path: '/playlist/:playlistId', name: 'playlist', component: PlaylistComponent },
  { path: '/:catchAll(.*)', redirect: '/' }, // Redirect unknown routes to home
  { path: '/login', name: 'login', component: LoginComponent },
  { path: '/signup', name: 'signup', component: SignupComponent },
  { path: '/account', name: 'account', component: AccountComponent },
  { path: '/verify/:email', name: 'VerifyEmail', component: verifyEmailComponent},
  { path: '/forgot-password', name: 'forgotPassword', component: ForgotPasswordComponent },
  { path: '/reset-password/:token', name: 'resetPassword', component: ResetPasswordComponent },
  { path: '/liked', name: 'likedSongs', component: LikedSongsComponent },
  { path: '/playlists', name: 'playlists', component: PlaylistsListComponent },
  { path: '/playlists/:id', name: 'playlistDetail', component: UserPlaylistDetailComponent },
  { path: '/g/:genre', name: 'genre', component: GenrePage}
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

const prodURL = process.env.VUE_APP_PROD_URL;

let skipNextPush = false;

let userInfoFetched = false; // Flag to track if user info has been fetched


router.beforeEach(async (to, from) => {
  const feedStore = useFeedStore();
  const userStore = useUserStore();
  // console.log('Navigating from', from.name, 'to', to.name);

  // 1️⃣ Fetch user info only once
  if (!userInfoFetched) {
    try {
      userStore.loadingUserInfo = true;
      const res = await fetch(`${prodURL}/auth/userInfo`, {
        method: 'GET',
        credentials: 'include'
      }).then(r => r.json());

      if (res.success) {
        userStore.setUser(res);
      }
      await feedStore.fetchMadeForYou();
      userInfoFetched = true;
    } catch (err) {
      console.error('Error fetching user info:', err);
    } finally {
      userStore.loadingUserInfo = false;
    }
  }

  const user = userStore.user;

  // 2️⃣ Force email verification
  if (
    user?.email &&
    !user.emailVerified &&
    !['VerifyEmail', 'login', 'forgotPassword', 'resetPassword'].includes(to.name)
  ) {
    // console.log('Redirecting to verify email:', user.email);
    return {
      name: 'VerifyEmail',
      params: { email: user.email },
      replace: true
    };
  }

  // 3️⃣ Redirect logged-in users away from auth pages
  if (
    user?.emailVerified &&
    ['login', 'signup'].includes(to.name)
  ) {
    return { name: 'home', replace: true };
  }

  // 4️⃣ Handle skipNextPush logic (SAFE)
  const isSameRoute =
    to.name === from.name &&
    JSON.stringify(to.params) === JSON.stringify(from.params);

  if (skipNextPush && isSameRoute) {
    skipNextPush = false;
    return false; // cancel duplicate navigation
  }

  // 5️⃣ Detect routes that should prevent immediate re-push
  if (
    from.name === 'NowPlaying' ||
    (from.name === 'search' && to.name === 'search')
  ) {
    skipNextPush = true;
  } else {
    skipNextPush = false;
  }
  // 6️⃣ Allow navigation
  return true;
});
export default router;